<template>
  <div
    class="author"
    :style="cssStyles"
  >
    <div
      v-if="hideAdditional"
      class="author__avatar"
    >
      <img
        class="author__img"
        :src="author.avatar"
        :alt="author.name"
        height="104"
        width="104"
      />
    </div>
    <div class="author__description">
      <h1 class="author__name">{{ author.name }}</h1>
      <div
        v-if="hideAdditional"
        class="author__about"
        v-html="author.about"
      />
      <div
        v-if="hideAdditional"
        class="author__social-networks"
      >
        <div
          v-for="social in authorSocials"
          :key="social.url"
          class="author-social-link"
        >
          <BHLink
            :href="social.url"
            target="_blank"
            class="author-social-link__link"
            @click="sendAnalytics(social)"
          >
            <span
              :class="getClassSocialLink(social)"
              class="author-social-link__icon"
            />
          </BHLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import type { IAuthor } from '~/types/api/archives';
  import BHLink from '~/components/BHLink.vue';

  interface ISocial {
    url: null | string;
    icon: string;
    name: string;
  }

  export default defineNuxtComponent({
    name: 'AuthorHeader',

    components: {
      BHLink,
    },

    inject: {
      // Категория в которой происходит событие
      // Для аналитики
      eventCategory: {
        default() {
          return () => '';
        },
      },
    },

    props: {
      author: {
        type: Object as PropType<IAuthor>,
        required: true,
      },

      hideAdditional: {
        type: Boolean as PropType<boolean>,
        required: true,
      },
    },

    computed: {
      cssStyles(): Record<string, string> {
        return {
          '--author-padding': this.hideAdditional ? '16px' : '12px 0px 0px',
        };
      },

      authorSocials(): Array<ISocial> {
        const socials: ISocial[] = [
          { url: this.author.vkontakte, icon: 'vk', name: 'Вк' },
          { url: this.author.twitter, icon: 'twitter', name: 'Твиттер' },
          { url: this.author.telegram, icon: 'telegram', name: 'Телеграмм' },
        ];
        return socials.filter((social) => Boolean(social.url));
      },
    },

    methods: {
      // Функция для аналитики клика на соцсети автора
      sendAnalytics(_social: ISocial) {
        // const currentSlug = getSlugFromUrl(this.$route.fullPath);
        // const slugSocial = getSlugFromUrl(social.url || '');
      },

      getClassSocialLink(social: ISocial): Record<string, boolean> {
        return {
          [`author-social-link__icon--${social.icon}`]: true,
        };
      },
    },
  });
</script>

<style lang="scss" scoped>
  .author {
    background: #ffffff;
    overflow: hidden;
    padding: var(--author-padding);

    @include max-tablet {
      padding-bottom: $spacing-6;
    }

    @include tablet {
      padding: 0;
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
    }

    &__social-networks {
      display: flex;
      justify-content: center;
      @include tablet {
        justify-content: flex-start;
      }
      &:has(.author-social-link) {
        margin-top: $spacing-4;
      }
      :nth-child(n + 2) {
        margin-left: 24px;
      }

      .author-social-link {
        display: block;
        width: 24px;
        height: 24px;
        cursor: pointer;
        &__icon {
          display: block;
          width: 100%;
          height: 100%;
          position: relative;
          &:before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            right: 0;
            bottom: 0;
            opacity: 0;
            z-index: 0;
          }
          @include hover {
            &:before {
              background: rgba(0, 0, 0, 0.2);
              opacity: 1;
              z-index: 1;
            }
          }
          &--telegram {
            @include tgIcon;

            &:before {
              @include tgIconMask;
            }
          }
          &--vk {
            @include vkIcon;

            &:before {
              @include vkIconMask;
            }
          }
          &--twitter {
            @include twIcon;

            &:before {
              @include twIconMask;
            }
          }
        }
      }
    }

    &__avatar {
      width: 135px;
      height: 130px;
      background-image: url('@/assets/images/svg/avatar-decoration.svg');
      background-size: contain;
      flex-shrink: 0;
      padding-top: 14px;
      padding-left: $spacing-1;
      margin: $spacing-0 auto $spacing-2;
      position: relative;

      @include tablet {
        margin-left: 0;
        margin-right: $spacing-6;
      }
    }

    &__img {
      position: absolute;
      border-radius: 50%;
      bottom: 9px;
      left: 3px;
      width: 104px;
      height: 104px;
      object-fit: cover;
      border: 4px solid #ffffff;
    }

    &__name {
      word-break: break-word;
      font-family: $headline-font--1;
      margin: 0 0 $spacing-2;
      text-align: center;
      @include headline-normal-text;

      @include tablet {
        margin: $spacing-3 0 $spacing-2;
        text-align: left;
        @include headline-big-text;
      }
    }

    &__about {
      font-family: $text-font--1;
      @include normalText;

      :deep() {
        a {
          @include link-styles;
        }
      }

      @include tablet {
        @include mediumText;
      }
    }
  }
</style>
